import React from 'react';

import { useSelect } from 'downshift';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import UnstyledButton from 'components/unstyled-button';

import {
  ClearIndicator,
  DropdownIndicator,
  IndicatorSeparator,
} from './mad-lib-select-accessories';
import theme from './theme';
import { IFilter } from './types';

interface IIsOpen {
  isOpen: boolean;
}
const Container = styled.div`
  grid-area: select;

  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  min-width: 0;
`;

const SelectContainer = styled.div<IIsOpen>`
  display: flex;
  flex-direction: column;
  flex-basis: 100%;
  padding: 0 0.5rem;
  box-shadow: ${p => (p.isOpen ? Styles.boxShadow : 'none')};
  border-radius: 8px 8px 0 0;

  ${Styles.desktop`
    flex-basis: 80%;
    padding: 0 1rem;
  `};
`;

const SelectContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  padding: 0.4rem 0;
  overflow: hidden;

  ${Styles.desktop`
    padding: 0.2rem 0;
  `}
`;

const BlackBar = styled.div<IIsOpen>`
  width: 100%;
  height: 1px;
  background-color: ${Styles.color.black};

  visibility: ${p => (p.isOpen ? 'hidden' : 'visible')};

  ${Styles.desktop`
    height: 3px;
  `}
`;

const SelectToggle = styled(UnstyledButton)`
  flex: 1;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  margin: 0;
  overflow: hidden;
`;

const SelectLabel = styled.label`
  margin: 0 0 0 0.5rem;
  text-transform: ${Styles.textTransform.headlines};
  font-family: ${Styles.fontFamily.base};

  font-size: 0.9rem;

  ${Styles.desktop`
    font-size: 1.4rem;
    letter-spacing: 1px;
    margin-left: 1rem;
  `}
`;

const textStyles = css`
  margin: 0;
  text-transform: ${Styles.textTransform.headlines};
  font-family: ${Styles.fontFamily.base};
  font-size: 1rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;

  ${Styles.desktop`
    font-size: 1.8rem;
    letter-spacing: 1px;
  `}
`;

const CurrentSelection = styled.h3<{ isDefault: boolean }>`
  ${textStyles};
  color: ${p => (p.isDefault ? theme.defaultSelectionColor : Styles.color.black)};
`;

const MenuContainer = styled.div`
  position: relative;
  width: 100%;
`;

const MenuList = styled.ul<IIsOpen>`
  position: absolute;
  top: 0;
  left: 0;
  z-index: ${Styles.zIndex.below};
  max-height: 12.5rem;
  width: 100%;

  list-style: none;
  padding: 0.2rem 0;
  margin: 0;
  overflow-y: auto;
  background-color: ${Styles.color.cardBackground};
  border-radius: 0 0 8px 8px;
  box-shadow: ${Styles.boxShadow};

  * + * {
    border-top: solid 1px ${Styles.color.grey.five};
  }

  display: ${p => (p.isOpen ? 'inherit' : 'none')};

  ${Styles.desktop`
    padding: 0;
    max-height: 20rem;
  `}
`;

const SelectItem = styled.li<{ isHighlighted: boolean; isSelected: boolean }>`
  padding: 0.3rem 0.5rem;
  color: ${p => (p.isSelected ? Styles.color.white : Styles.color.black)};

  & :hover {
    background-color: ${p => (!p.isSelected ? Styles.color.grey.five : Styles.color.primary)};
  }
  background-color: ${p =>
    p.isSelected ? Styles.color.primary : p.isHighlighted ? Styles.color.grey.five : 'inherit'};

  ${Styles.desktop`
    padding: 0.3rem 1rem;
  `};

  cursor: pointer;
`;

const SelectItemText = styled.h3`
  ${textStyles};
`;

const itemToString = (filter: IFilter | null) => filter?.description?.locale ?? '';

interface IMadLibSelect {
  prompt?: string;
  selectedFilter: IFilter | null;
  filters: IFilter[];
  onFilterChange: (filter?: IFilter | null) => void;
}

export const MadLibSelect: React.FC<IMadLibSelect> = ({
  onFilterChange,
  selectedFilter,
  filters,
  prompt,
}) => {
  const { formatMessage } = useIntl();
  const {
    isOpen,
    selectedItem,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
    reset,
  } = useSelect({
    items: filters,
    itemToString,
    selectedItem: selectedFilter,
    onSelectedItemChange: changes => onFilterChange(changes.selectedItem),
  });

  const currentSelection = selectedItem
    ? selectedItem.description?.locale
    : formatMessage({ id: 'selectAFilter' });

  return (
    <Container>
      <SelectLabel {...getLabelProps()} aria-hidden>
        {prompt || formatMessage({ id: 'imCraving' })}
      </SelectLabel>
      <SelectContainer isOpen={isOpen}>
        <SelectContent>
          <SelectToggle {...getToggleButtonProps()}>
            <CurrentSelection isDefault={!selectedItem}>{currentSelection}</CurrentSelection>
            <DropdownIndicator isOpen={isOpen} />
          </SelectToggle>
          <IndicatorSeparator />
          <ClearIndicator onClick={reset} />
        </SelectContent>
        <BlackBar isOpen={isOpen} />
      </SelectContainer>
      <MenuContainer>
        <MenuList {...getMenuProps()} isOpen={isOpen}>
          {isOpen &&
            filters.map((filter, index) => (
              <SelectItem
                key={filter._id}
                isSelected={selectedItem?._id === filter._id}
                isHighlighted={highlightedIndex === index}
                {...getItemProps({
                  item: filter,
                  index,
                })}
              >
                <SelectItemText>{filter.description?.locale}</SelectItemText>
              </SelectItem>
            ))}
        </MenuList>
      </MenuContainer>
    </Container>
  );
};
export default MadLibSelect;
